import {ReactElement, useEffect} from "react";
import {Navigation} from "components/UIComponents/Navigation";
import {FormattedMessage} from "react-intl";
import {PrimaryButton} from "components/UIComponents/PrimaryButton";
import {SubTitle} from "components/UIComponents/SubTitle";
import {ReactComponent as CloseIcon} from "components/Icons/close.svg";
import {WinePickerToolbar} from "./WinePickerToolbar";
import {WinePickerResults} from "./WinePickerResults";
import {Configure, InstantSearch} from "react-instantsearch-dom";
import {useAppDispatch, useAppSelector} from "lib/redux/hooks";
import {winePickerActions, winePickerSelectors} from "./winePickerSlice";
import {AlgoliaWine} from "./winePickerModel";
import {useAlgoliaClient, useAlgoliaProductIndex} from "features/algolia/algoliaHooks";
import {algoliaCredentialsSelectors} from "features/algolia/algoliaCredentialsSlice";
import {Spinner} from "components/UIComponents/Spinner";
import {authSelectors} from "features/auth/authSlice";
import {isEmpty, keys} from "ramda";
import {WinePickerFavorites} from "./WinePickerFavorites";
import {isReady} from "lib/Loadable";
import {ErrorBoundary} from "../../components/UIComponents/ErrorBoundary";
import {Language} from "../i18n/translations";


export interface WinePickerProps {
    onClose: () => void
    onPick: (pickedWines: AlgoliaWine[]) => void
    language: Language
}

export function WinePicker(props: WinePickerProps): ReactElement | null {
    const dispatch = useAppDispatch();
    const authenticatedCustomer = useAppSelector(authSelectors.customer);
    const favoriteWinesRequestPending = useAppSelector(winePickerSelectors.favoriteWineRequestPending);
    const favoriteWinesRequestError = useAppSelector(winePickerSelectors.favoriteWineRequestError);
    const favoriteWines = useAppSelector(winePickerSelectors.favoriteWines);
    const category = useAppSelector(winePickerSelectors.category);
    const searchTerm = useAppSelector(winePickerSelectors.searchTerm);
    const favoriteWinesShowed = useAppSelector(winePickerSelectors.isFavoriteWinesShowed);
    const favoritesWinesAvailable = useAppSelector(winePickerSelectors.favoriteWinesAvailable);
    const pickedWines = useAppSelector(winePickerSelectors.pickedWines);
    const noPickedWines = keys(pickedWines).length;
    const algoliaIndexName = useAppSelector(algoliaCredentialsSelectors.productIndex);
    const algoliaClient = useAlgoliaClient();
    const algoliaWineIndex = useAlgoliaProductIndex();

    useEffect(() => {
        if (
            authenticatedCustomer !== favoriteWines?.customer &&
            authenticatedCustomer != null &&
            !favoriteWinesRequestPending &&
            favoriteWinesRequestError == null &&
            isReady(algoliaWineIndex)
        ) {
            dispatch(winePickerActions.fetchFavoriteWines({
                userId: authenticatedCustomer,
                algoliaProductIndex: algoliaWineIndex.loaded
            }));
        }
    }, [favoriteWines, favoriteWinesRequestPending, favoriteWinesRequestError, authenticatedCustomer, algoliaWineIndex, dispatch]);

    if (!isReady(algoliaClient) || algoliaIndexName == null) {
        return (
            <div className="w-full h-full flex flex-col items-center justify-center bg-white">
                <Spinner className="w-10 h-10"/>
            </div>
        );
    }

    const onPick = () => {
        const pickedWineList = Object.values(pickedWines);
        dispatch(winePickerActions.reset());
        props.onPick(pickedWineList);
        props.onClose();
    };

    let filteredFavoriteWines: AlgoliaWine[] = [];
    if (favoritesWinesAvailable) {
        filteredFavoriteWines = favoriteWines!.wines;
        if (category) {
            filteredFavoriteWines = filteredFavoriteWines.filter(w=>w.category===category);
        }
        if (searchTerm) {
            const searchIn: (keyof AlgoliaWine)[] = [
                'name', 'nameFr', 'nameIt',
                'country', 'countryFr', 'countryIt',
                'region', 'regionFr', 'regionIt'
            ];
            filteredFavoriteWines = filteredFavoriteWines.filter(w=>
                searchIn.some(k =>
                    w[k]
                    && typeof w[k] === "string"
                    && (w[k] as string).toLowerCase().includes(searchTerm.toLowerCase())
                )
            );
        }
    }

    return (
        <div className="w-full h-full flex flex-col items-stretch bg-white">
            <div className="flex-none">
                <Navigation
                    left={<>
                        <SubTitle><FormattedMessage id="winepicker.navigation.title"/></SubTitle>
                    </>}
                    right={<>
                        <div className="flex-auto font-semibold py-4 leading-snug text-right">
                            {noPickedWines > 0 && (
                                <span className="flex-none">
                                        <FormattedMessage
                                            id="winepicker.results.noPickedWines"
                                            values={{noPicked: noPickedWines}}
                                        />
                                    </span>
                            )}
                        </div>
                        <PrimaryButton onClick={onPick}>
                            <FormattedMessage id="winepicker.navigation.chose"/>
                        </PrimaryButton>
                        <span/>
                        <button onClick={props.onClose}><CloseIcon/></button>
                    </>}
                />
            </div>

            <ErrorBoundary>
                <InstantSearch
                    searchClient={algoliaClient.loaded}
                    indexName={`${algoliaIndexName}-name-asc`}
                >
                    <Configure
                        hitsPerPage={12}
                        typoTolerance={true}
                        facets={[]}
                    />

                    <div className="flex-none">
                        <WinePickerToolbar/>
                    </div>

                    <div className="flex-auto overflow-y-auto pl-11">
                        {/* double div because else we have the scroll bar on 2/3 width */}
                        <div className="w-2/3">
                            {favoritesWinesAvailable && favoriteWinesShowed && (
                                <WinePickerFavorites favorites={favoriteWines!.wines}/>
                            )}
                            {!(favoritesWinesAvailable && favoriteWinesShowed) && <>
                                {!isEmpty(filteredFavoriteWines)&&
                                    <WinePickerFavorites favorites={filteredFavoriteWines}/>
                                }
                                <WinePickerResults language={props.language}/>
                            </>}
                        </div>
                    </div>
                </InstantSearch>
            </ErrorBoundary>
        </div>
    );
}
